// THE LONG KINDLING — Room 16: "The Old Toolshed" (Act II) — the watchers' hands.
// Authored from research/wall/script/v3/rooms_11-16.md §Room 16 and research/wall/GAME_MAP.md §16.
//
// THE EDGE (an interior). The one warm-FEELING room at the Edge — which is the trap (warmth is what
// the cold is for). Geometry is adapted art-first to public/art/toolshed.png (a low shed; shelves and
// sills crowded with little whittled animals worn smooth; a workbench with tools; a heavy maul; a
// window at right; a lantern hanging top-left; the doorsill at the lower edge where the fresh-cut dog
// is left).
//
// Painting is 384x216; the room is 480x270, so ART coords scale to ROOM coords by x1.25.
// No watch-fire in this interior — NO firemask. A small warm lantern-glow only (the trap-warmth).
//
// This room is a DISCOVERY to EARN, not a puzzle: pick up THE MAUL (gates R13's latch-stake) and
// THE FRESH CARVED DOG (the kin token, carried to the R17 edge climax in Rowan's pocket). Both are
// `take` hotspots setting cross-room GLOBAL flags (`maul`, `carved_dog`). The tempting WRONG way is
// the returned tenderness — leave/carry a gift back OUT to the watcher — which visibly fails
// (un-shuns him a notch); modelled as a reversible boolean `gift_given` (false=keep his gift, give
// nothing back, RIGHT; true=gift returned, WRONG). No soft-lock: both keys are here, reachable from
// R12; the wrong-way toggle reverts.
import { loadRoom, type Room } from "../../core/schema";

export const toolshed = {
  id: "toolshed",
  name: "The Old Toolshed",
  size: { w: 480, h: 270 },
  background: { color: 0x1a140c },
  bgImage: "art/toolshed.png", // Retro-Diffusion painting (cosy interior — the trap); aligned to it
  // hero starts on the plank floor, by the door (lower-centre)
  spawn: { x: 240, y: 250 },
  actorRadius: 8,
  interactRadius: 64,
  // the toolshed-truth: false = keep his gift, give nothing back (RIGHT — use his love, don't relieve
  // him); true = a tenderness returned out to the line (the WRONG way — un-shuns him a notch). Reversible.
  state: { gift_given: false },
  // walkable floor = the plank/tile floor across the lower frame, between the benches
  walkmesh: [
    [ { x: 40, y: 210 }, { x: 440, y: 210 }, { x: 440, y: 266 }, { x: 40, y: 266 } ],
  ],
  hotspots: [
    // ---- THE MAUL — pick it up (gates R13's latch-stake; sets global `maul`)
    { id: "the_maul", verb: "take", label: "the heavy maul", interactAt: { x: 340, y: 230 },
      line: "A heavy driving-maul, oiled and ready, set by a hand that kept it for a reason. This is what re-drives the latch-stake out at the last reach. I take it up; it's the weight of the work the line needs.",
      effect: { setGlobal: { maul: true } },
      polygon: [ { x: 320, y: 150 }, { x: 356, y: 150 }, { x: 356, y: 200 }, { x: 320, y: 200 } ] },

    // ---- THE FRESH-CUT LONG-EARED DOG on the inside of the doorsill — pick it up (kin token; global `carved_dog`)
    { id: "carved_dog", verb: "take", label: "the fresh-cut carved dog", interactAt: { x: 240, y: 240 },
      line: "New wood — pale and sharp where the others are worn dark. A long-eared dog: MY dog, the one I dragged everywhere till the ears fell off. Set on the INSIDE of the sill, where only something that came up to the house in the night could have left it. He carved it out there at the line, in the cold, and brought it to where I'd find it. He remembers me. He's trying to BE my grandda — from out there at the edge. I pocket it, and I keep it.",
      effect: { setGlobal: { carved_dog: true } },
      polygon: [ { x: 218, y: 196 }, { x: 262, y: 196 }, { x: 262, y: 214 }, { x: 218, y: 214 } ] },

    // ---- the whittled animals (the watchers' hands — the whole shunned line of wards)
    { id: "whittled_animals", verb: "examine", label: "the whittled animals", interactAt: { x: 120, y: 228 },
      line: "Shelves of little carved beasts — hare, fox, a long-eared dog — all worn smooth by small hands long dead. Every ward who walked out to the line left his carvings behind. These are their hands — the whole shunned line of them — all that's left of them on this side of the treeline. My da had one like this. And his da. The same hand, lifetime back, made the oldest of them.",
      polygon: [ { x: 40, y: 40 }, { x: 200, y: 40 }, { x: 200, y: 130 }, { x: 40, y: 130 } ] },

    // ---- the bench & tools (he STILL carves — fresh curls of pale wood; out at the line, in the cold)
    { id: "bench_tools", verb: "examine", label: "the bench & tools", interactAt: { x: 200, y: 230 },
      line: "Good tools, oiled by someone still. A whittling knife with fresh curls of pale wood under it — new work. He still carves. Out there at the line, in the cold, he still makes toys for a house that won't let him in.",
      polygon: [ { x: 110, y: 150 }, { x: 240, y: 150 }, { x: 240, y: 192 }, { x: 110, y: 192 } ] },

    // ---- the maker's mark (under the oldest carvings — the same mark sanded off the home lintel, R2)
    { id: "makers_mark", verb: "examine", label: "the maker's mark", interactAt: { x: 90, y: 230 },
      line: "The same mark cut small under the oldest animals — and it's the mark sanded off our lintel at home. The first carver of these toys and the name we un-write are the same man — the First Ward. He's OURS. He's my blood, standing out there with his back to us. One day my hands will be among these.",
      polygon: [ { x: 48, y: 130 }, { x: 110, y: 130 }, { x: 110, y: 168 }, { x: 48, y: 168 } ] },

    // ---- the tempting WRONG way: return a tenderness OUT to the line (visibly fails; reversible)
    { id: "return_gift", verb: "use", label: "leave a gift back for him", interactAt: { x: 300, y: 240 },
      line: "The room invites a tenderness — set one of his carvings out on the OUTER sill for him, the way you'd return a kindness. But by morning the frost has come up the path to the doorstep that took the gift, the worn channel toward the treeline running fresher, and the First Ward stands a half-step nearer the house, his back not quite so squarely turned. A given gift is a door opened. I bring it back inside — keep his gift, give nothing back.",
      effect: { toggle: ["gift_given"] },
      polygon: [ { x: 276, y: 196 }, { x: 326, y: 196 }, { x: 326, y: 214 }, { x: 276, y: 214 } ] },
  ],
  // living-scene fx: NO watch-fire here. A small warm lantern-glow (the cosy trap-warmth) and faint
  // dust-motes in the still wooden air. The far line is felt, not shown.
  fx: [
    { id: "lantern_glow", kind: "glow", layer: "back", x: 56, y: 40, w: 30, h: 30, color: 0xffc066, intensity: 0.7, seed: 1 },
    { id: "dust_motes", kind: "motes", layer: "front", x: 140, y: 120, w: 160, h: 80, count: 12, intensity: 0.5, seed: 2 },
  ],
  // No NPCs present — the First Ward is felt entirely through his WORK (the worn toys, the oiled tools,
  // the fresh curls, the new dog on the inside of the sill). He is most a person in this room.
  npcs: [],
  // Exits back to the Cleared Ground (R12).
  exits: [
    { id: "to_cleared", target: "cleared_ground", spawn: { x: 240, y: 250 }, transition: "fade",
      polygon: [ { x: 210, y: 240 }, { x: 270, y: 240 }, { x: 270, y: 264 }, { x: 210, y: 264 } ] },
  ],
};

export const room: Room = loadRoom(toolshed);
